import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PageTitle from "../../components/page-title";
import { alertAdd } from "../../redux/actions";
import Can from "../../components/can";
import { Redirect } from "react-router-dom";
import {getAxiosInstance} from "../../redux/common";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 800,
    margin: "0 auto",
  },
  uploadArea: {
    border: "2px dashed #ccc",
    borderRadius: 4,
    padding: theme.spacing(6),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    "&.dragging": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  input: {
    display: "none",
  },
  uploadIcon: {
    fontSize: 64,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  fileName: {
    marginTop: theme.spacing(2),
    fontWeight: "bold",
  },
  buttonGroup: {
    marginTop: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
  },
  previewSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
  },
  errorBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#ffebee",
    border: "1px solid #f44336",
    borderRadius: 4,
    color: "#c62828",
  },
}));

const ImportGabosPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [selectedFile, setSelectedFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "text/xml" || file.name.endsWith(".xml")) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError("Proszę wybrać plik XML");
        setSelectedFile(null);
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      if (file.type === "text/xml" || file.name.endsWith(".xml")) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError("Proszę wybrać plik XML");
        setSelectedFile(null);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handlePreview = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setPreview(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/gabos-import/preview", {
        method: "POST",
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Błąd podczas podglądu formularza");
      }

      const data = await response.json();
      setPreview(data);
      dispatch(
        alertAdd({
          text: "Podgląd formularza załadowany pomyślnie",
          isSuccess: true,
        })
      );
    } catch (err) {
      setError(err.message);
      dispatch(
        alertAdd({
          text: `Błąd: ${err.message}`,
          isError: true,
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await getAxiosInstance().post("/api/gabos-import/upload", formData);
      const data = response.data;

      dispatch(
        alertAdd({
          text: `Formularz "${data.formName}" został zaimportowany pomyślnie`,
          isSuccess: true,
        })
      );

      // Redirect to forms list
      history.push("/forms");
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Błąd podczas importu formularza";
      setError(errorMessage);
      dispatch(
        alertAdd({
          text: `Błąd: ${errorMessage}`,
          isError: true,
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    history.push("/forms");
  };

  const page = () => (
    <Box className={classes.root}>
      <Box p={1}>
        <PageTitle title="Importuj formularz z Gabos" />
      </Box>

      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Wybierz plik XML z systemu Gabos
        </Typography>

        <Typography variant="body2" color="textSecondary" paragraph>
          Plik XML powinien zawierać definicję formularza w formacie Gabos.
          Obecnie obsługiwane są tylko pola tekstowe (typ 0).
        </Typography>

        {error && (
          <Box className={classes.errorBox}>
            <Typography variant="body2">
              <strong>Błąd:</strong> {error}
            </Typography>
          </Box>
        )}

        <div
          className={`${classes.uploadArea} ${dragging ? "dragging" : ""}`}
          onClick={() => document.getElementById("file-input").click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            id="file-input"
            type="file"
            accept=".xml,text/xml"
            className={classes.input}
            onChange={handleFileSelect}
          />

          <CloudUploadIcon className={classes.uploadIcon} />

          <Typography variant="h6">
            {selectedFile
              ? "Kliknij aby zmienić plik"
              : "Kliknij lub przeciągnij plik XML tutaj"}
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Akceptowane formaty: .xml
          </Typography>

          {selectedFile && (
            <Typography className={classes.fileName} color="primary">
              Wybrany plik: {selectedFile.name}
            </Typography>
          )}
        </div>

        {preview && (
          <Box className={classes.previewSection}>
            <Typography variant="h6" gutterBottom>
              Podgląd formularza
            </Typography>
            <Typography variant="body2">
              <strong>Nazwa:</strong> {preview.name}
            </Typography>
            <Typography variant="body2">
              <strong>Liczba pól:</strong>{" "}
              {preview.layoutElementObject?.children?.length || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Formularz będzie zapisany jako wersja robocza (DRAFT).
            </Typography>
          </Box>
        )}

        <Box className={classes.buttonGroup}>
          <Button variant="outlined" onClick={handleCancel} disabled={uploading}>
            Anuluj
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={handlePreview}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? "Wczytuję..." : "Podgląd"}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? "Importuję..." : "Importuj"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Can
      permission="form-add-page:view"
      ok={page}
      not={() => <Redirect to="/" />}
    />
  );
};

export default ImportGabosPage;
